import get from 'ts-get';

import { useRutilusQuery } from '../../../../../../api/graphql/useRutilusQuery';
import { Heading } from '../../../../../../components/Heading/Heading';
import { LoadingSpinner } from '../../../../../../components/LoadingSpinner/LoadingSpinner';
import { MapIcon } from '../../../../../../components/Maps/components/MapIcon';
import { getWaypointByTypeId } from '../../../../../../components/Maps/helpers/getWaypointByTypeId';
import { NextImageWrapper } from '../../../../../../components/NextImage/NextImageWrapper';
import { Text } from '../../../../../../components/Text/Text';
import { useClientHasRendered } from '../../../../../../helpers/hooks';
import { GetWaypoint, GetWaypointVariables } from '../../../../../../interfaces/graphql';
import { getWaypoint } from '../../../../../../queries/rutilus/Waypoint';
import { useExplore } from '../../../../context/ExploreContext';
import { MapDrawerLayout } from '../../MapDrawerLayout';
import { MapDrawerNotification } from '../../MapDrawerNotification/MapDrawerNotification';

export interface IProps {
  waypointId: string;
}

export const WaypointView = ({ waypointId }: IProps) => {
  const { exploreQueryParams } = useExplore();
  const clientHasRendered = useClientHasRendered();
  const { data, loading: isLoading } = useRutilusQuery<GetWaypoint, GetWaypointVariables>(
    getWaypoint,
    {
      variables: { id: waypointId },
    },
  );

  const description = get(data, d => d.waypoint.description, '');
  const name = get(data, d => d.waypoint.name, '');
  const typeId = get(data, d => d.waypoint.symbol, '');
  const imageUrl = get(data, d => d.waypoint.image.url, '');
  const imageWidth = get(data, d => d.waypoint.image.width, 0);
  const imageHeight = get(data, d => d.waypoint.image.height, 0);

  if (!clientHasRendered || (!data && isLoading)) {
    return (
      <MapDrawerLayout>
        <LoadingSpinner loadingText="Loading waypoint..." />
      </MapDrawerLayout>
    );
  }

  if (!data) {
    return (
      <MapDrawerLayout>
        <div className="flex h-full flex-col">
          <div className="flex">
            <Heading variant="sm" className="pr-2">
              Waypoint
            </Heading>
          </div>
          <MapDrawerNotification title="It looks like something went wrong" />
        </div>
      </MapDrawerLayout>
    );
  }

  return (
    <MapDrawerLayout
      className="!p-0"
      closeButtonClassName="mt-4 mr-4"
      variant={imageUrl ? 'controlOnColorfulBg' : 'controlLakeFogBg'}
    >
      {imageUrl && (
        <NextImageWrapper
          wrapperClassName="col-start-1 row-start-1 mb-4"
          customPlaceholderClassName="rounded-r-lg md:rounded-l-lg md:rounded-r-none"
          alt="Waypoint picture"
          src={imageUrl}
          width={imageWidth}
          height={imageHeight}
        />
      )}

      <div className={`relative ${imageUrl ? 'top-[-33px]' : 'top-[25px]'} left-[20px]`}>
        <MapIcon
          name={getWaypointByTypeId(typeId).icon}
          alt="waypoint icon"
          scale={1.3}
          className="absolute"
          mapStyle={exploreQueryParams.mapStyle}
        />
      </div>

      <div className={`ml-4 mr-6 space-y-2 ${imageUrl ? '-mt-4' : 'mt-10'}`}>
        <Heading variant="xs">{name}</Heading>
        <Text className="pt-2">{description}</Text>
      </div>
    </MapDrawerLayout>
  );
};
